/**
 * useTranslation Hook
 * 
 * A custom React hook for accessing translations in components.
 * This provides a clean, scalable API for internationalization.
 * 
 * Usage:
 * ```jsx
 * const { t, language, setLanguage } = useTranslation();
 * 
 * return <h1>{t('welcome_message')}</h1>;
 * ```
 */

import { useContext, useCallback } from 'react';
import LanguageContext from '../helper/LanguageContext';
import { translate, getAvailableLanguages, isLanguageSupported } from '../Languages';

/**
 * Custom hook for translations
 * @returns {Object} - Translation utilities
 */
const useTranslation = () => {
  const { language, setLanguage, isGerm, setIsGerm } = useContext(LanguageContext);
  
  // Determine the current language code
  // Support both new 'language' property and legacy 'isGerm' boolean
  const currentLanguage = language || (isGerm ? 'de' : 'en');
  
  /**
   * Translate a key to the current language
   * @param {string} key - The translation key
   * @param {Object} params - Optional parameters for interpolation (future use)
   * @returns {string} - The translated string
   */
  const t = useCallback((key, params = {}) => {
    let translation = translate(key, currentLanguage);
    
    // Simple parameter interpolation (e.g., t('hello_user', { name: 'John' }))
    // Replaces {{paramName}} with the param value
    if (params && typeof translation === 'string') {
      Object.keys(params).forEach(param => {
        translation = translation.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
      });
    }
    
    return translation;
  }, [currentLanguage]);
  
  /**
   * Change the application language
   * @param {string} langCode - The language code to switch to
   */
  const changeLanguage = useCallback((langCode) => {
    if (!isLanguageSupported(langCode)) {
      console.warn(`Language '${langCode}' is not supported. Falling back to 'en'.`);
      langCode = 'en';
    }
    
    // Update both new and legacy state for backward compatibility
    if (setLanguage) {
      setLanguage(langCode);
    }
    if (setIsGerm) {
      setIsGerm(langCode === 'de');
    }
    
    // Store preference
    localStorage.setItem('switch', langCode === 'de' ? '2' : '1');
    localStorage.setItem('language', langCode);
  }, [setLanguage, setIsGerm]);
  
  return {
    t,
    language: currentLanguage,
    setLanguage: changeLanguage,
    availableLanguages: getAvailableLanguages(),
    isGerm: currentLanguage === 'de', // Legacy support
  };
};

export default useTranslation;

